import { translationKey } from '../i18n';
import { useEffect, useRef, useState } from 'react';
import { getDeviceUuid } from '../api';

const PENDING = 'pill_reminder_pending_alarm_auto_confirm';
const DONE = 'pill_reminder_completed_alarm_requests';
const inFlight = new Map();
const savingDoses = new Map();

function read(key, fallback) {
    try { return JSON.parse((key === DONE ? localStorage : sessionStorage).getItem(key)) || fallback; } catch { return fallback; }
}

function normalizeAlarmRequest(message) {
    if (message?.type !== 'AUTO_CONFIRM_MEDICATION_ALARM') return null;
    const tokens = Array.isArray(message.logIds) ? message.logIds : String(message.logIds || '').split(',');
    if (!tokens.length || tokens.some(id => !/^\d+$/.test(String(id).trim()) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0)) return null;
    const logIds = [...new Set(tokens.map(Number))].sort((a, b) => a - b);
    const deviceUuid = message.deviceUuid || getDeviceUuid();
    return { ...message, deviceUuid, logIds,
        requestId: message.requestId || `legacy:${deviceUuid}:${logIds.join(',')}` };
}

function ack(requestId) {
    try { window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'ALARM_CONFIRM_FINISHED', requestId })); } catch {}
}

export default function useAlarmConfirmation(confirm, onNativeSaved) {
    const dismissed = useRef(new Set());
    const callback = useRef(confirm);
    callback.current = confirm;
    const nativeSavedCallback = useRef(onNativeSaved);
    nativeSavedCallback.current = onNativeSaved;
    const [failure, setFailure] = useState(null);
    const [revision, setRevision] = useState(0);
    const retry = () => {
        if (!failure) return;
        dismissed.current.delete(failure.requestId);
        sessionStorage.removeItem(`alarm-failed:${failure.requestId}`);
        setFailure(null);
        setRevision(value => value + 1);
    };

    useEffect(() => {
        let mounted = true;
        const loadQueue = () => {
            const value = read(PENDING, []);
            return (Array.isArray(value) ? value : [value]).map(normalizeAlarmRequest).filter(Boolean);
        };
        const saveQueue = queue => sessionStorage.setItem(PENDING, JSON.stringify(queue));
        const pump = async () => {
            for (const request of loadQueue()) {
                if (!mounted) return;
                try { window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'ALARM_CONFIRM_RECEIVED', requestId: request.requestId })); } catch {}
                const completed = read(DONE, {});
                if (completed[request.requestId]) {
                    saveQueue(loadQueue().filter(item => item.requestId !== request.requestId));
                    ack(request.requestId);
                    setFailure(current => current?.requestId === request.requestId ? null : current);
                    try { sessionStorage.removeItem(`alarm-failed:${request.requestId}`); } catch {}
                    try {
                        if (request.nativeSaved && request.deviceUuid === getDeviceUuid()) {
                            nativeSavedCallback.current?.(request);
                        }
                    } catch {}
                    continue;
                }
                if (sessionStorage.getItem(`alarm-failed:${request.requestId}`)) {
                    const detail = read(`alarm-failed:${request.requestId}`, {});
                    if (!dismissed.current.has(request.requestId)) setFailure({ ...request, message: detail?.message });
                    continue;
                }
                let work = inFlight.get(request.requestId);
                if (!work) {
                    // Publish the promise before invoking callbacks: native messages can re-enter.
                    work = Promise.resolve().then(async () => {
                        if (request.deviceUuid !== getDeviceUuid()) throw new Error(translationKey('다른 기기의 알림이에요. 현재 계정을 확인해주세요.'));
                        const doseKey = `${request.deviceUuid}:${request.logIds.join(',')}`;
                        let saving = savingDoses.get(doseKey);
                        if (!saving) {
                            saving = Promise.resolve().then(() => callback.current(request.logIds, request.confirmedAt || null));
                            savingDoses.set(doseKey, saving);
                        }
                        try { await saving; }
                        finally { if (savingDoses.get(doseKey) === saving) savingDoses.delete(doseKey); }
                        localStorage.setItem(DONE, JSON.stringify({ ...read(DONE, {}), [request.requestId]: true }));
                        saveQueue(loadQueue().filter(item => item.requestId !== request.requestId));
                        sessionStorage.removeItem(`alarm-failed:${request.requestId}`);
                        ack(request.requestId);
                    });
                    inFlight.set(request.requestId, work);
                }
                try {
                    await work;
                    if (mounted) setFailure(current => current?.requestId === request.requestId ? null : current);
                } catch (error) {
                    sessionStorage.setItem(`alarm-failed:${request.requestId}`, JSON.stringify({ message: error?.message || null }));
                    if (mounted) setFailure({ ...request, message: error?.message });
                    continue;
                } finally {
                    if (inFlight.get(request.requestId) === work) inFlight.delete(request.requestId);
                }
            }
        };
        const receive = event => {
            try {
                const request = normalizeAlarmRequest(typeof event.data === 'string' ? JSON.parse(event.data) : event.data);
                if (!request) return;
                const queue = loadQueue();
                if (!queue.some(item => item.requestId === request.requestId)) saveQueue([...queue, request]);
                else if (request.nativeSaved) {
                    // A saved result can arrive after this request was queued as a fallback.
                    saveQueue(queue.map(item => item.requestId === request.requestId
                        && item.deviceUuid === request.deviceUuid && item.logIds.join(',') === request.logIds.join(',')
                        ? { ...item, nativeSaved: true } : item));
                }
                void pump();
            } catch {}
        };
        window.addEventListener('message', receive);
        document.addEventListener('message', receive);
        void pump();
        return () => {
            mounted = false;
            window.removeEventListener('message', receive);
            document.removeEventListener('message', receive);
        };
    }, [revision]);

    const dismiss = () => {
        // Dismiss only the error UI; retain the request for explicit retry after a reload.
        if (failure) dismissed.current.add(failure.requestId);
        setFailure(null);
    };
    return { failure, retry, dismiss };
}
